import React from "react";
import { CheckCircle2, Clock, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusConfigDefault = {
  success: { icon: CheckCircle2, color: "text-emerald-400", glow: "hover:shadow-emerald-500/30", label: "Success" },
  pending: { icon: Clock, color: "text-amber-400", glow: "hover:shadow-amber-500/30", label: "Pending" },
  processing: { icon: Loader2, color: "text-cyan-400", glow: "hover:shadow-cyan-500/30", label: "Processing", animate: true },
  failed: { icon: XCircle, color: "text-red-400", glow: "hover:shadow-red-500/30", label: "Failed" },
  expired: { icon: AlertTriangle, color: "text-slate-400", glow: "hover:shadow-slate-500/30", label: "Expired" },
  delivered: { icon: CheckCircle2, color: "text-emerald-400", glow: "hover:shadow-emerald-500/30", label: "Completed" },
  retrying: { icon: Loader2, color: "text-orange-400", glow: "hover:shadow-orange-500/30", label: "Retry", animate: true },
};

const getStatusConfig = (t) => {
  if (!t) return statusConfigDefault;
  return {
    success: { ...statusConfigDefault.success, label: t("success") },
    pending: { ...statusConfigDefault.pending, label: t("pending") },
    processing: { ...statusConfigDefault.processing, label: t("processing") },
    failed: { ...statusConfigDefault.failed, label: t("failed") },
    expired: { ...statusConfigDefault.expired, label: t("expired") },
    delivered: { ...statusConfigDefault.delivered, label: t("completed") },
    retrying: { ...statusConfigDefault.retrying, label: t("retry") },
  };
};

export default function StatusBadge({ status, size = "default" }) {
  let t = null;
  try {
    const langContext = useLanguage();
    t = langContext?.t;
  } catch (e) {}

  const statusConfig = getStatusConfig(t);
  const statusKey = (status || "").toLowerCase();
  const config = statusConfig[statusKey] || statusConfig.pending;
  const Icon = config.icon;

  const sizeClasses =
    size === "small"
      ? "px-3 py-1 text-xs gap-1.5"
      : "px-4 py-1.5 text-sm gap-2";

  return (
    <span
      className={`
        relative inline-flex items-center rounded-full
        border border-cyan-900/60
        bg-[#0B1C2D]
        ${config.color}
        ${sizeClasses}
        font-semibold
        backdrop-blur-md
        transition-all duration-300 ease-in-out
        hover:border-cyan-400
        hover:shadow-lg
        ${config.glow}
        hover:-translate-y-0.5
        hover:scale-105
        cursor-pointer
      `}
    >
      {/* subtle inner glow */}
      <span className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500/5 to-blue-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300"></span>

      <Icon
        className={`relative h-4 w-4 ${
          config.animate ? "animate-spin" : ""
        } transition-transform duration-300 group-hover:rotate-6`}
      />

      <span className="relative tracking-wide">{config.label}</span>
    </span>
  );
}